import { useMemo } from 'react';

import { useSavedSizesCatalog } from '@air/feature-saved-sizes/hooks';
import {
  getSizeDimensionsLabel,
  type SavedSize,
  type SizeSet,
  SOCIAL_MEDIA_PLATFORM_GROUPS,
  type SocialMediaFormat,
  type SocialMediaPlatformGroup,
} from '@air/feature-saved-sizes/utils';

import { type MentionItem } from '../../ui/components/ChatComposer/mentions/types';

type PlatformFormatEntry = {
  format: SocialMediaFormat;
  group: SocialMediaPlatformGroup;
};

const PLATFORM_FORMATS_BY_KEY = new Map<string, PlatformFormatEntry>(
  SOCIAL_MEDIA_PLATFORM_GROUPS.flatMap((group) =>
    group.formats.map((format): [string, PlatformFormatEntry] => [format.id, { format, group }]),
  ),
);

/** Derived from `SizeSet` (not imported from `@air/api` directly) — see CLAUDE.md on the local SDK link. */
type SizeSetMember = SizeSet['members'][number];

/** A member the workspace can no longer resolve (deleted saved size, unknown platform key) is dropped. */
const getSizeSetMemberLabel = (member: SizeSetMember, sizePresetsById: Map<string, SavedSize>): string | null => {
  if (member.type === 'sizePreset') {
    const sizePreset = sizePresetsById.get(member.sizePresetId);

    return sizePreset ? `${getSizeDimensionsLabel(sizePreset)} px` : null;
  }

  const entry = PLATFORM_FORMATS_BY_KEY.get(member.platformKey);

  return entry
    ? `${entry.group.label} · ${entry.format.chipLabel ?? entry.format.label} (${entry.format.ratio})`
    : null;
};

const isResolvedLabel = (label: string | null): label is string => label !== null;

const mapSizeSetToMentionItem = (sizeSet: SizeSet, sizePresetsById: Map<string, SavedSize>): MentionItem => ({
  id: sizeSet.id,
  memberCount: sizeSet.members.length,
  memberLabels: sizeSet.members.map((member) => getSizeSetMemberLabel(member, sizePresetsById)).filter(isResolvedLabel),
  name: sizeSet.name,
  thumbnailUrl: null,
  type: 'set',
});

const mapSavedSizeToMentionItem = (savedSize: SavedSize): MentionItem => ({
  aspectRatio: savedSize.width / savedSize.height,
  dimensionsLabel: `${getSizeDimensionsLabel(savedSize)} px`,
  id: savedSize.id,
  name: savedSize.name,
  thumbnailUrl: null,
  type: 'size',
});

export type UseSizeMentionsOptions = {
  /** Suppresses the saved-sizes queries when the rollout flag is off, so a disabled workspace never calls the endpoints. */
  enabled: boolean;
  workspaceId: string;
};

/**
 * Sizes/sets stay separate from `useBrandKitMentions` so callers can order them first.
 */
export const useSizeMentions = ({ enabled, workspaceId }: UseSizeMentionsOptions) => {
  const { isLoading: isSavedSizesLoading, sizePresets, sizeSets } = useSavedSizesCatalog({ enabled, workspaceId });

  const sizePresetsById = useMemo(
    () => new Map(sizePresets.map((sizePreset) => [sizePreset.id, sizePreset])),
    [sizePresets],
  );

  // Sets lead: reaching for a whole campaign beats reaching for one of its sizes. Publishing before
  // the catalog resolves would drop every member, leaving a set that expands to a bare name.
  const sizeMentions = useMemo(
    () =>
      isSavedSizesLoading
        ? []
        : [
            ...sizeSets.map((sizeSet) => mapSizeSetToMentionItem(sizeSet, sizePresetsById)),
            ...sizePresets.map(mapSavedSizeToMentionItem),
          ],
    [isSavedSizesLoading, sizePresets, sizePresetsById, sizeSets],
  );

  return { sizeMentions };
};
